import React, { useState, useEffect, useContext, useRef } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate, Link } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import CoreFeed from "./components/CoreFeed";
import RecognitionForm from "./components/RecognitionForm";
import TeamsPage from "./pages/TeamsPage";
import AchievementsPage from "./pages/AchievementsPage";
import UserProfile from "./pages/UserProfile";
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import PasswordRecovery from "./pages/PasswordRecovery";
import PasswordReset from "./pages/PasswordReset";
import AdminPage from "./pages/AdminPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import CampaignPage from "./pages/CampaignPage";
import CampaignNominationsPage from "./pages/CampaignNominationsPage";
import RecognitionPage from "./pages/RecognitionPage";
import WalletPage from "./pages/WalletPage";
import MarketplacePage from "./pages/MarketplacePage";
import NominationsPage from "./pages/NominationsPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import SettingsPage from "./pages/SettingsPage";
import InsightsPage from "./pages/InsightsPage";
import ProtectedRoute from "./components/ProtectedRoute";
import LicenseAgreementModal from "./components/LicenseAgreementModal";
import DevBoardPage from "./pages/DevBoardPage";

import { AuthProvider } from "./context/AuthContext";
import { I18nFromAuth, useTranslation } from "./i18n";
import AuthContext from "./context/AuthContext";
import { OrgThemeProvider } from "./context/OrgThemeContext";
import OrgThemeContext from "./context/OrgThemeContext";
import { TenantProvider } from "./context/TenantContext";
import TenantContext, { getApexHost, getSubdomain, TENANT_REDIRECT_DISABLED } from "./context/TenantContext";
import axios from "axios";
import { Award, Bell, Search, ChevronDown, ChevronRight, User, LogOut, Home, Settings, Users, Wallet, ShoppingBag, Trophy, CheckCircle, Heart, Building2 } from "lucide-react";
import Sidebar from "./components/Sidebar";
import WidgetsFlyout from "./components/widgets/WidgetsFlyout";
import { RAIL_CUTOFF } from "./components/widgets/WidgetRail";
import NotificationBell from "./components/NotificationBell";
import UserAvatar from "./components/UserAvatar";
import NotificationsPage from "./pages/NotificationsPage";
import ChatPage from "./pages/ChatPage";
import WorkvivoKudoPage from "./pages/WorkvivoKudoPage";
import { motion, AnimatePresence } from "framer-motion";
import "./index.css";

// Import Workvivo components
import WorkvivoPeople from "./components/WorkvivoPeople";
import WorkvivoPosts from "./components/WorkvivoPosts";
import WorkvivoLoading from "./components/WorkvivoLoading";

// Import responsive components
import { useResponsive } from './components/responsive';

// Mirrors the rail cutover in index.css: above this width the expanded
// sidebar fits inside the feed's left gutter and covers nothing.

// Create a React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30, // 30 seconds
      gcTime: 1000 * 60 * 5, // 5 minutes (renamed from cacheTime in react-query v5)
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

const pageTitles = {
  "/": "titles.root",
  "/recognition": "titles.recognition",
  "/teams": "titles.teams",
  "/achievements": "titles.achievements",
  "/profile": "titles.profile",
  "/profile/me": "titles.profile_me",
  "/admin": "titles.admin",
  "/login": "titles.login",
  "/notifications": "titles.notifications",
  "/search": "titles.search",
  "/admin/workvivo": "titles.admin_workvivo",
  "/workvivo/posts": "titles.workvivo_posts",
  "/workvivo/people": "titles.workvivo_people",
  "/workvivo/kudos": "titles.workvivo_kudos",
  "/wallet": "titles.wallet",
  "/marketplace": "titles.marketplace",
  "/nominations": "titles.nominations",
  "/approvals": "titles.approvals",
  "/super-admin": "titles.super_admin",
  "/settings": "titles.settings",
};

const pageIcons = {
  "/": <Home className="mr-2" size={22} />,
  "/recognition": <Heart className="mr-2" size={22} />,
  "/teams": <Users className="mr-2" size={22} />,
  "/achievements": <Award className="mr-2" size={22} />,
  "/profile": <User className="mr-2" size={22} />,
  "/profile/me": <User className="mr-2" size={22} />,
  "/admin": <Settings className="mr-2" size={22} />,
  "/login": null,
  "/notifications": <Bell className="mr-2" size={22} />,
  "/search": <Search className="mr-2" size={22} />,
  "/workvivo": <Award className="mr-2" size={22} />,
  "/workvivo/posts": <Users className="mr-2" size={22} />,
  "/workvivo/people": <Users className="mr-2" size={22} />,
  "/wallet": <Wallet className="mr-2" size={22} />,
  "/marketplace": <ShoppingBag className="mr-2" size={22} />,
  "/nominations": <Trophy className="mr-2" size={22} />,
  "/approvals": <CheckCircle className="mr-2" size={22} />,
  "/super-admin": <Building2 className="mr-2" size={22} />,
  "/settings": <Settings className="mr-2" size={22} />
};

// Taglines displayed beneath the page title in the TopBar.
const pageTaglines = {
  "/": "taglines.root",
  "/recognition": "taglines.recognition",
  "/teams": "taglines.teams",
  "/achievements": "taglines.achievements",
  "/profile": "taglines.profile",
  "/profile/me": "taglines.profile_me",
  "/admin": "taglines.admin",
  "/notifications": "taglines.notifications",
  "/wallet": "taglines.wallet",
  "/marketplace": "taglines.marketplace",
  "/nominations": "taglines.nominations",
  "/approvals": "taglines.approvals",
  "/super-admin": "taglines.super_admin",
  "/settings": "taglines.settings",
};

const TopBar = ({ onToggleSidebar, isSidebarExpanded }) => {
  const { t } = useTranslation("nav");
  const location = useLocation();
  const { user, logout } = useContext(AuthContext);
  const orgTheme = useContext(OrgThemeContext);
  const [isProfileMenuOpen, setProfileMenuOpen] = useState(false);
  const [viewedProfileName, setViewedProfileName] = useState(null);
  const dropdownRef = useRef(null);

  // Listen for profileViewed events dispatched by UserProfile page
  useEffect(() => {
    const handleProfileViewed = (e) => {
      if (e.detail?.isOwnProfile) {
        setViewedProfileName(null);
      } else {
        setViewedProfileName(e.detail?.name || null);
      }
    };
    window.addEventListener('profileViewed', handleProfileViewed);
    return () => window.removeEventListener('profileViewed', handleProfileViewed);
  }, []);

  // Reset when navigating away from profile pages
  useEffect(() => {
    if (!location.pathname.startsWith('/profile/')) {
      setViewedProfileName(null);
    }
  }, [location.pathname]);

  // Find the current page path and title
  const currentPath = Object.keys(pageTitles).find(
    (key) => (location.pathname.startsWith(key) && key !== "/") || location.pathname === key
  ) || "/";

  const currentPageTitle = t(pageTitles[currentPath]);
  const currentPageIcon = pageIcons[currentPath];
  const currentPageTagline = pageTaglines[currentPath] ? t(pageTaglines[currentPath]) : undefined;

  // For other users' profiles, show their name instead of "My Profile"
  const displayTitle = location.pathname.startsWith('/profile/') && viewedProfileName
    ? `${viewedProfileName}'s Profile`
    : currentPageTitle;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  const handleSignOut = () => {
    setProfileMenuOpen(false);
    logout();
  };

  const getUserDisplayName = () => {
    return user?.full_name || user?.name || user?.email || 'User';
  };

  return (
    <div
      id="app-topbar"
      className="fixed top-0 left-0 w-full py-1 text-white shadow-lg z-50"
      style={{ background: `linear-gradient(to right, ${orgTheme.primaryColor}, ${orgTheme.secondaryColor})` }}
    >
      {/* Layout differs by breakpoint:
            - Mobile (<md): logo-left / page-title-center (tap to open menu) /
              bell-right. Hamburger and profile dropdown are NOT shown — the
              page-title tap opens the full-screen menu where profile/settings
              live at the bottom.
            - Desktop (md+): hamburger / inline logo / separator / page title +
              tagline / bell / profile dropdown — same as before.
       */}
      <div className="relative flex items-center justify-between">
        <div className="flex items-center flex-1 min-w-0">
          {/* Desktop-only sidebar toggle */}
          <button
            className="hidden md:flex ml-2 text-white hover:bg-white/20 p-2 rounded-full transition-colors duration-75 flex-shrink-0"
            onClick={onToggleSidebar}
            aria-label={isSidebarExpanded ? t("topbar.collapse_sidebar") : t("topbar.expand_sidebar")}
          >
            {isSidebarExpanded ? <ChevronRight size={20} /> : <ChevronDown size={20} />}
          </button>

          {/* Mobile logo — top-left, small. */}
          <Link
            to="/"
            className="md:hidden flex items-center overflow-hidden flex-shrink-0 px-2 py-1 hover:opacity-80 transition-opacity"
            aria-label={t("topbar.go_to_feed")}
          >
            {orgTheme.logoUrl && (
              <img src={orgTheme.logoUrl} alt={t("topbar.org_logo_alt")} className="h-8 max-w-[72px] w-auto object-contain" />
            )}
          </Link>

          {/* Desktop logo — inline, full size, sits left of the separator/title. */}
          <Link to="/" className="hidden md:flex items-center justify-center overflow-hidden flex-shrink-0 px-3 hover:opacity-80 transition-opacity" aria-label={t("topbar.go_to_feed")}>
            {orgTheme.logoUrl && (
              <img src={orgTheme.logoUrl} alt={t("topbar.org_logo_alt")} className="h-16 max-w-[200px] w-auto object-contain" />
            )}
          </Link>

          {/* Desktop separator + page title + tagline (single inline block) */}
          <div className="hidden md:flex items-center min-w-0">
            <div className="w-1 h-9 bg-white flex-shrink-0 rounded-full" />
            <div className="flex items-center ml-3 min-w-0">
              {currentPageIcon}
              <div className="flex flex-col min-w-0">
                <div className="flex items-center">
                  <h1 className="text-xl font-bold leading-tight truncate">{displayTitle}</h1>
                  {location.pathname.split('/').length > 2 &&
                    location.pathname !== "/profile/me" &&
                    !location.pathname.startsWith("/profile/") &&
                    !location.pathname.startsWith("/admin/campaigns/") && (
                    <div className="flex items-center ml-2 text-white/70">
                      <span className="mx-2">/</span>
                      <span className="font-medium">
                        {location.pathname.split('/').pop().replace(/[-_]/g, ' ')}
                      </span>
                    </div>
                  )}
                </div>
                {currentPageTagline && (
                  <p className="text-xs text-white/80 leading-tight truncate mt-0.5">
                    {currentPageTagline}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile centered page title (tappable — opens the full-screen menu).
            Absolutely positioned so it stays viewport-centered regardless of
            the logo / bell widths flanking it. The ChevronDown rotates when
            the menu is open, hinting at the dropdown behavior. */}
        <button
          type="button"
          onClick={onToggleSidebar}
          className="md:hidden absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 px-2 py-1 rounded-full hover:bg-white/10 transition-colors max-w-[calc(100%-160px)]"
          aria-label={`${isSidebarExpanded ? "Close" : "Open"} navigation menu`}
          aria-expanded={isSidebarExpanded}
        >
          <span className="text-xs font-semibold leading-tight truncate">
            {displayTitle}
          </span>
          <ChevronDown
            size={14}
            className={`flex-shrink-0 transition-transform duration-200 ${isSidebarExpanded ? "rotate-180" : ""}`}
          />
        </button>

        {/* Right side. On mobile: just the notification bell. On desktop:
            bell + profile dropdown (profile is moved INTO the mobile menu). */}
        <div className="flex items-center justify-end md:min-w-[200px] pr-2 flex-shrink-0">
        <NotificationBell />
        <div className="relative mr-2 hidden md:block" ref={dropdownRef}>
          <button
            className="flex items-center p-1.5 hover:bg-white/20 rounded-full transition-colors duration-75"
            onClick={() => setProfileMenuOpen(!isProfileMenuOpen)}
          >
            <UserAvatar
              src={user?.avatar_url}
              name={getUserDisplayName()}
              className="w-9 h-9 rounded-full object-cover border-2 border-white/50 shadow-md"
              textClassName="text-sm"
            />
            <span className="hidden md:block text-sm font-medium ml-2 mr-1">{getUserDisplayName()}</span>
            <ChevronDown
              size={16}
              className={`hidden md:block transition-transform duration-200 ${isProfileMenuOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Profile Dropdown */}
          <AnimatePresence>
            {isProfileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                className="absolute right-0 mt-3 w-56 rounded-2xl shadow-2xl overflow-hidden z-50 border border-purple-100"
                style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #faf5ff 100%)',
                }}
              >
                {/* User Info Header */}
                <div className="px-4 py-4" style={{ background: `linear-gradient(to right, ${orgTheme.primaryColor}, ${orgTheme.secondaryColor})` }}>
                  <div className="flex items-center">
                    <UserAvatar
                      src={user?.avatar_url}
                      name={getUserDisplayName()}
                      className="w-12 h-12 rounded-full object-cover border-2 border-white/50 shadow-lg"
                      textClassName="text-sm"
                    />
                    <div className="ml-3 overflow-hidden">
                      <p className="text-white font-semibold truncate">{getUserDisplayName()}</p>
                      <p className="text-white/70 text-xs truncate">{user?.email || ''}</p>
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-2">
                  <Link
                    to="/profile/me"
                    className="flex items-center px-4 py-3 hover:bg-purple-50 transition-colors duration-75 group"
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    <div className="w-8 h-8 rounded-2xl bg-purple-100 flex items-center justify-center mr-3 group-hover:bg-purple-200 transition-colors duration-75">
                      <User size={16} className="text-purple-600" />
                    </div>
                    <span className="text-gray-700 font-medium group-hover:text-purple-700 transition-colors duration-75">{t("topbar.my_profile")}</span>
                  </Link>
                  <Link
                    to="/settings"
                    className="flex items-center px-4 py-3 hover:bg-purple-50 transition-colors duration-75 group"
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    <div className="w-8 h-8 rounded-2xl bg-purple-100 flex items-center justify-center mr-3 group-hover:bg-purple-200 transition-colors duration-75">
                      <Settings size={16} className="text-purple-600" />
                    </div>
                    <span className="text-gray-700 font-medium group-hover:text-purple-700 transition-colors duration-75">{t("topbar.settings")}</span>
                  </Link>
                </div>

                {/* Sign Out Button */}
                <div className="px-3 py-3 border-t border-purple-100">
                  <button
                    className="flex items-center justify-center w-full px-4 py-2.5 rounded-full org-gradient transition-all duration-200 shadow-md hover:shadow-lg"
                    onClick={handleSignOut}
                  >
                    <LogOut size={16} className="text-white mr-2" />
                    <span className="text-white font-medium">{t("topbar.sign_out")}</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        </div>
      </div>
    </div>
  );
};

const Layout = ({ children, onOpenForm }) => {
  const location = useLocation();
  const responsive = useResponsive();
  const orgTheme = useContext(OrgThemeContext);
  // Hide sidebar and topbar on all auth pages (login, signup, password recovery/reset)
  const isAuthPage = ["/login", "/signup", "/password-recovery", "/reset-password"].includes(location.pathname);
  // The feed lays itself out with .app-grid (nav gutter | feed | widget rail),
  // so it clears the sidebar on its own. Every other page uses .page-shell.
  const isFeedPage = location.pathname === "/";
  const [isSidebarExpanded, setSidebarExpanded] = useState(false);

  // Scroll to top whenever the route changes — fixes the "page opens half-way down" issue
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  const [topBarHeight, setTopBarHeight] = useState(0);

  // Measure actual top bar height so content is never obscured
  useEffect(() => {
    const el = document.getElementById('app-topbar');
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setTopBarHeight(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height);
    });
    observer.observe(el);
    setTopBarHeight(el.getBoundingClientRect().height);
    return () => observer.disconnect();
  }, [isAuthPage]);

  // The sidebar no longer moves content: it occupies a constant 80px gutter and
  // overlays when expanded, so there is nothing to measure. Above the rail
  // cutover the expanded w-64 panel still fits inside the feed's left gutter and
  // covers nothing, so it can default open there. Below it the grid collapses to
  // one column and an expanded panel would sit on top of the feed, so it starts
  // closed. RAIL_CUTOFF is defined once in WidgetRail and mirrored by the
  // .app-grid breakpoint in index.css, which cannot import it.
  const railLayoutActive = responsive.width >= RAIL_CUTOFF;
  useEffect(() => {
    setSidebarExpanded(railLayoutActive);
  }, [railLayoutActive]);

  // Close sidebar on mobile when navigating
  useEffect(() => {
    if (responsive.isMobile) {
      setSidebarExpanded(false);
    }
  }, [location.pathname, responsive.isMobile]);

  const toggleSidebar = (value) => {
    const newState = typeof value === 'boolean' ? value : !isSidebarExpanded;
    setSidebarExpanded(newState);
    
    // Dispatch event for AdminPage to listen to
    window.dispatchEvent(
      new CustomEvent('sidebarToggled', { detail: { isExpanded: newState } })
    );
  };

  return (
    <div
      className="flex flex-col min-h-screen relative bg-gray-50 overflow-x-hidden"
      data-sidebar={!isAuthPage && isSidebarExpanded ? "expanded" : "collapsed"}
    >
      {/* Top Bar */}
      {!isAuthPage && (
        <TopBar
          onToggleSidebar={toggleSidebar}
          isSidebarExpanded={isSidebarExpanded}
        />
      )}

      <div className="flex flex-1">
        {/* Sidebar - Hidden on auth pages */}
        {!isAuthPage && (
          <Sidebar
            onOpenForm={onOpenForm}
            isExpanded={isSidebarExpanded}
            toggleSidebar={toggleSidebar}
            topBarHeight={topBarHeight}
          />
        )}

        {/* Main Content Area. Spans the full viewport width — the sidebar is a
            fixed overlay — so pages can centre on the viewport rather than on
            whatever the sidebar left behind. */}
        <main
          className="flex-1 min-h-screen overflow-x-hidden"
          style={{
            paddingTop: !isAuthPage && topBarHeight ? topBarHeight : undefined,
          }}
        >
          <div className={`h-full w-full ${!isAuthPage && !isFeedPage ? 'page-shell' : ''}`}>
            {children}
          </div>
        </main>

        {/* Below the rail cutover the widgets leave the page and live behind
            this button, tucked under the notification bell.

            Feed only, matching the desktop rail — which renders inside
            CoreFeed and so has never appeared anywhere else. A widgets button
            floating over the wallet or the admin tabs offered the feed's
            furniture on pages the feed has nothing to do with. */}
        {isFeedPage && <WidgetsFlyout topOffset={topBarHeight} />}
      </div>
    </div>
  );
};

// SSO callback handler: checks for ?sso_code= or ?sso_error= and handles accordingly
const SSOCallbackHandler = ({ children }) => {
  const { t } = useTranslation("nav");
  const { loginWithSSOCode, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  // Start in the "processing" state when an SSO code is present in the URL, so
  // the full-screen loader shows from the very FIRST render. Otherwise the app
  // briefly renders /login (a protected route bouncing there) while the
  // token-exchange POST is still in flight — the "it only logs in once the call
  // finishes / stuck on login on mobile" symptom.
  const hasSsoParam = () => {
    const p = new URLSearchParams(window.location.search);
    return Boolean(p.get("sso_code") || p.get("login_code"));
  };
  const [processing, setProcessing] = useState(hasSsoParam);
  const startedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // login_code is the cross-host handoff (apex → tenant subdomain).
    // sso_code is the existing IDP-callback handoff. Both are short-lived
    // signed envelopes that wrap an access token, redeemed via the same
    // /sso/token-exchange endpoint.
    const ssoCode = params.get("sso_code") || params.get("login_code");
    const ssoError = params.get("sso_error");
    // Where to land after the exchange. Set by the SAML ACS (relayed from the
    // deep link the user tapped) or by the cross-host handoff. Validated as a
    // same-site relative path — it goes straight into a navigation.
    const nextParam = params.get("next");
    const target =
      nextParam &&
      nextParam.startsWith("/") &&
      !nextParam.startsWith("//") &&
      !nextParam.startsWith("/\\")
        ? nextParam
        : "/";

    // Handle SSO error redirects (server redirects to /?sso_error=xxx)
    if (ssoError) {
      window.history.replaceState({}, document.title, "/");
      navigate(`/login?error=${ssoError}`, { replace: true });
      return;
    }

    if (ssoCode && !isAuthenticated && !startedRef.current) {
      startedRef.current = true;
      setProcessing(true);
      // Clean the URL immediately
      window.history.replaceState({}, document.title, "/");

      loginWithSSOCode(ssoCode)
        .then(() => {
          // Hard navigation (not client-side navigate) so AuthProvider
          // re-initializes with the freshly-stored token. A client-side navigate
          // can still land on /login on this first pass because the just-set
          // auth state hasn't propagated to ProtectedRoute yet — that's the
          // "have to click twice" symptom.
          window.location.replace(target);
        })
        .catch((err) => {
          console.error("Token exchange failed:", err);
          setProcessing(false);
          navigate("/login?error=auth_failed", { replace: true });
        });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (processing) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t("sso.completing")}</p>
        </div>
      </div>
    );
  }

  return children;
};

// Once the user is authenticated, make sure they're on the right host.
// - If the user's org has a subdomain and we're not on it, request a one-time
//   transfer code and redirect to <subdomain>.<apex>/?login_code=...
// - If we're on a tenant subdomain whose org doesn't match the user, log out
//   (cross-tenant token reuse).
// Runs at most once per page load.
const TenantRedirectGate = ({ children }) => {
  const { user, isAuthenticated, logout } = useContext(AuthContext);
  const { scopedOrgId, loading: tenantLoading } = useContext(TenantContext);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    if (tenantLoading || !isAuthenticated || !user) return;

    const currentSub = getSubdomain();
    const userSub = user?.organization_customization?.subdomain || null;
    const userOrgId = user?.organization_id || null;

    // Mismatch: token belongs to a different org than this subdomain.
    // Superusers bypass so platform admins can manage any tenant.
    if (
      scopedOrgId &&
      userOrgId &&
      scopedOrgId !== userOrgId &&
      !user.is_superuser
    ) {
      ranRef.current = true;
      logout();
      window.location.replace("/login?error=wrong_org");
      return;
    }

    // User has a subdomain bound to their org but we're not on it.
    if (!TENANT_REDIRECT_DISABLED && userSub && userSub !== currentSub && !user.is_superuser) {
      ranRef.current = true;
      const apex = getApexHost();
      const target = `https://${userSub}.${apex}`;
      const apiBase = process.env.REACT_APP_API_URL || "";
      const token = localStorage.getItem("token");
      axios
        .post(
          `${apiBase}/api/v1/auth/transfer-code`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .then((res) => {
          const code = res.data?.code;
          // Clear the token from this origin so a stale session doesn't
          // linger after we hand off to the subdomain.
          localStorage.removeItem("token");
          sessionStorage.removeItem("token");
          // Carry the path across the hop so a deep link opened on the apex
          // still resolves after the handoff.
          const path =
            window.location.pathname + window.location.search + window.location.hash;
          const nextQs =
            path && path !== "/" ? `&next=${encodeURIComponent(path)}` : "";
          window.location.replace(
            code
              ? `${target}/?login_code=${encodeURIComponent(code)}${nextQs}`
              : target,
          );
        })
        .catch(() => {
          // If the transfer code fails, fall back to a plain redirect.
          // The user will need to log in again on the subdomain.
          localStorage.removeItem("token");
          sessionStorage.removeItem("token");
          window.location.replace(target);
        });
    }
  }, [isAuthenticated, user, scopedOrgId, tenantLoading, logout]);

  return children;
};

const LicenseGate = ({ children }) => {
  const { user, logout, refreshUser, isAuthenticated } = useContext(AuthContext);

  const needsLicense =
    isAuthenticated &&
    user?.role === "admin" &&
    !user?.is_superuser &&
    user?.license_accepted === false;

  const handleAccepted = async () => {
    try {
      const token = localStorage.getItem("token");
      const apiUrl = process.env.REACT_APP_API_URL || "";
      await fetch(`${apiUrl}/api/v1/users/me/accept-license`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      await refreshUser();
    } catch {
      // If accept fails, user can retry
    }
  };

  if (!needsLicense) return children;

  return (
    <>
      {children}
      <LicenseAgreementModal onAccepted={handleAccepted} onDeclined={logout} />
    </>
  );
};

function MainApp() {
  const [isFormOpen, setFormOpen] = useState(false);
  const [isWorkvivoConnecting, setWorkvivoConnecting] = useState(false);

  // Connection state is managed by setWorkvivoConnecting directly when needed
  // eslint-disable-next-line no-unused-vars
  const _setWorkvivoConnecting = setWorkvivoConnecting; // Keep for future use

  // Handle recognition form submission
  const handleRecognitionSubmit = async (formData) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';

      // Use the user selected in the form
      if (!formData.recognized_user_id) {
        throw new Error('Please select a user to recognize');
      }

      const recognitionData = {
        message: formData.message,
        points_amount: formData.points || 0,
        recipient_id: formData.recognized_user_id,
        recognition_type_id: formData.recognition_type_id || null,
        team_id: formData.team_id || null,
        visibility: formData.visibility || "org",
        visible_team_id: formData.visible_team_id || null,
        post_to_workvivo: !!formData.post_to_workvivo,
        workvivo_space_external_ids: formData.workvivo_space_external_ids ?? null,
        gif_id: formData.gif_id || null,
        points_hidden: (formData.points || 0) > 0 ? !formData.make_points_public : false,
      };

      const createResponse = await fetch(`${apiUrl}/api/v1/recognitions/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(recognitionData)
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create recognition');
      }

      const createdJson = await createResponse.json();

      // If the user asked to cross-post and the kudo failed, surface a
      // friendly error so they can report it. The recognition itself is
      // already created — the Workvivo post is best-effort.
      if (
        recognitionData.post_to_workvivo &&
        createdJson?.workvivo &&
        createdJson.workvivo.status !== 'posted'
      ) {
        const detail = createdJson.workvivo.last_error || 'Unknown error';
        alert(
          `Your recognition was created, but posting to Workvivo failed.\n\n${detail}\n\nPlease report this to AddFlint. You can retry the Workvivo post from the recognition card.`
        );
      }

      // Close the form and show success
      setFormOpen(false);
      const pointsAmount = recognitionData.points_amount || 0;
      if (pointsAmount > 0) {
        alert('Recognition created! Points are pending team admin approval.');
      } else {
        alert('Recognition created successfully!');
      }

      // Trigger a refresh of the feed component
      window.dispatchEvent(new CustomEvent('recognitionCreated'));

    } catch (error) {
      console.error('Error creating recognition:', error);
      alert('Failed to create recognition: ' + error.message);
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <TenantProvider>
        <AuthProvider>
          <I18nFromAuth>
          <SSOCallbackHandler>
          <OrgThemeProvider>
          <TenantRedirectGate>
          <LicenseGate>
          <Layout onOpenForm={() => setFormOpen(true)}>
            {/* Workvivo Loading Overlay */}
            <AnimatePresence>
              {isWorkvivoConnecting && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 bg-white"
                >
                  <WorkvivoLoading />
                </motion.div>
              )}
            </AnimatePresence>

            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/password-recovery" element={<PasswordRecovery />} />
              <Route path="/reset-password" element={<PasswordReset />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <CoreFeed onOpenForm={() => setFormOpen(true)} />
                  </ProtectedRoute>
                }
              />
              <Route path="/recognition" element={<ProtectedRoute><RecognitionPage /></ProtectedRoute>} />
              <Route path="/teams" element={<ProtectedRoute><TeamsPage /></ProtectedRoute>} />
              <Route path="/achievements" element={<ProtectedRoute><AchievementsPage /></ProtectedRoute>} />
              <Route path="/profile/:id" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />

              {/* New feature routes */}
              <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
              <Route path="/insights" element={<ProtectedRoute><InsightsPage /></ProtectedRoute>} />
              <Route path="/marketplace" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
              <Route path="/nominations" element={<ProtectedRoute><NominationsPage /></ProtectedRoute>} />
              <Route path="/approvals" element={<ProtectedRoute><ApprovalsPage /></ProtectedRoute>} />
              {/* Admin panel - accessible to org admins and superusers */}
              <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminPage /></ProtectedRoute>} />
              <Route path="/admin/campaigns/:id" element={<ProtectedRoute requireAdmin><CampaignPage /></ProtectedRoute>} />
              <Route path="/admin/campaigns/:id/nominations" element={<ProtectedRoute requireAdmin><CampaignNominationsPage /></ProtectedRoute>} />
              {/* Super Admin panel - superuser only */}
              <Route path="/super-admin" element={<ProtectedRoute requireSuperAdmin><SuperAdminPage /></ProtectedRoute>} />

              <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
              <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
              <Route path="/chat/:conversationId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
              <Route path="/search" element={<ProtectedRoute><div>Search Results</div></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

              {/* Workvivo user-facing routes */}
              <Route path="/workvivo/posts" element={<ProtectedRoute><WorkvivoPosts /></ProtectedRoute>} />
              <Route path="/workvivo/people" element={<ProtectedRoute><WorkvivoPeople /></ProtectedRoute>} />
              <Route path="/workvivo/kudos/:suggestionId" element={<ProtectedRoute><WorkvivoKudoPage /></ProtectedRoute>} />
            </Routes>
          </Layout>
          {/* Recognition Form Modal */}
          {isFormOpen && (
            <RecognitionForm
              isOpen={isFormOpen}
              onClose={() => setFormOpen(false)}
              onSubmit={handleRecognitionSubmit}
            />
          )}
        </LicenseGate>
        </TenantRedirectGate>
        </OrgThemeProvider>
        </SSOCallbackHandler>
          </I18nFromAuth>
        </AuthProvider>
        </TenantProvider>
      </Router>
      {/* React Query Devtools - only in development */}
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

// The Dev Board is a fully standalone workspace: it has its own shared-passphrase
// gate and must render WITHOUT the app's auth/tenant/layout chrome. Short-circuit
// before any of those providers mount. (Hard entry point — a full page load at
// /dev-board; not client-side navigated to.)
function App() {
  if (
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/dev-board")
  ) {
    return <DevBoardPage />;
  }
  return <MainApp />;
}

export default App;
